import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router';
import { Tabs } from '@/components/application/tabs/tabs';
import { PdfViewer } from '@/components/application/pdf-viewer/pdf-viewer';
import { Badge } from '@/components/base/badges/badges';
import { ArrowLeft, AlertCircle, CheckCircle, MagicWand01 } from '@untitledui/icons';
import { documentService } from '@/data/mock-documents';
import { Button } from '@/components/base/buttons/button';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { containerService } from '@/data/mock-containers';
import { setContainerResolved, isContainerResolved } from '@/data/mock-triage-state';
import { useTranslation } from 'react-i18next';

// PDF di test ora servito dalla cartella public
const testPdfUrl = "/document-review-test.pdf";

const COMMON_ATTRIBUTES = [
    'invoiceNumber',
    'amount',
    'currency',
    'issueDate',
    'dueDate',
    'shipper',
    'consignee',
    'containerNumber',
    'blNumber',
];

export const ShipperSingleDocumentTriagePage = () => {
    const { t } = useTranslation(['pages:documents', 'pages:containers', 'common']);
    const navigate = useNavigate();
    const { containerId, documentId } = useParams<{ containerId: string; documentId: string }>();

    const documents = useMemo(() => (containerId ? documentService.getByContainerId(containerId) : []), [containerId]);
    const fallbackId = documents[0]?.id;

    const [selectedId, setSelectedId] = useState<string>(documentId || fallbackId);

    // Stato per alert per documento e discrepanze
    const [docAlerts, setDocAlerts] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {};
        documents.forEach((d) => { initial[d.id] = Math.random() > 0.65; });
        return initial;
    });
    const [docDiscrepancies, setDocDiscrepancies] = useState<Record<string, Set<string>>>(() => {
        const init: Record<string, Set<string>> = {};
        documents.forEach((d) => {
            const s = new Set<string>();
            COMMON_ATTRIBUTES.forEach((k) => { if (Math.random() > 0.65) s.add(k); });
            init[d.id] = s;
        });
        return init;
    });

    useEffect(() => {
        if (!documents.find((d) => d.id === selectedId) && fallbackId) {
            setSelectedId(fallbackId);
        }
    }, [documents, selectedId, fallbackId]);

    // Reset mock data se container non è risolto
    useEffect(() => {
        if (containerId && !isContainerResolved(containerId)) {
            // Ripristina alert random
            const initial: Record<string, boolean> = {};
            documents.forEach((d) => { initial[d.id] = Math.random() > 0.65; });
            setDocAlerts(initial);
            // Ripristina discrepanze random
            const init: Record<string, Set<string>> = {};
            documents.forEach((d) => {
                const s = new Set<string>();
                COMMON_ATTRIBUTES.forEach((k) => { if (Math.random() > 0.65) s.add(k); });
                init[d.id] = s;
            });
            setDocDiscrepancies(init);
        }
    }, [containerId, documents]);

    const selectedDoc = documents.find((d) => d.id === selectedId);

    // I documenti mock non espongono un fileUrl; usiamo un PDF di test
    const docFileUrl = (testPdfUrl as string);

    const [formValues, setFormValues] = useState<Record<string, string | number>>(() => {
        if (!selectedDoc) return {};
        const base: Record<string, string | number> = {};
        COMMON_ATTRIBUTES.forEach((k) => {
            base[k] = (selectedDoc as any).extractedData?.[k] ?? '';
        });
        return base;
    });

    useEffect(() => {
        if (!selectedDoc) return;
        const next: Record<string, string | number> = {};
        COMMON_ATTRIBUTES.forEach((k) => {
            next[k] = (selectedDoc as any).extractedData?.[k] ?? '';
        });
        setFormValues(next);
    }, [selectedDoc]);

    const discrepancies = docDiscrepancies[selectedId] ?? new Set<string>();

    const tabItems = [
        { id: 'visual', label: t('triage.tabs.visual') },
        { id: 'raw', label: t('triage.tabs.raw') },
    ];

    // Dati container per mostrare containerNumber
    const containerNumber = useMemo(() => {
        if (!containerId) return containerId;
        const c = containerService.getById(containerId);
        return c?.containerNumber || containerId;
    }, [containerId]);

    // Modal & progress state
    const [openAI, setOpenAI] = useState(false);
    const [progress, setProgress] = useState(0);

    const startAIRun = () => {
        setOpenAI(true);
        setProgress(0);
        // Simulazione avanzamento
        const step = 12;
        const timer = setInterval(() => {
            setProgress((p) => {
                const next = Math.min(100, p + step);
                if (next === 100) {
                    clearInterval(timer);
                }
                return next;
            });
        }, 250);
    };

    const onCloseModal = (value: boolean) => {
        setOpenAI(value);
        if (!value && progress === 100) {
            // Aggiorna mock: rimuovi alert e discrepanze per tutti i documenti
            const clearedAlerts: Record<string, boolean> = {};
            documents.forEach((d) => { clearedAlerts[d.id] = false; });
            setDocAlerts(clearedAlerts);

            const clearedDiscr: Record<string, Set<string>> = {};
            documents.forEach((d) => { clearedDiscr[d.id] = new Set<string>(); });
            setDocDiscrepancies(clearedDiscr);
            // Salva stato risoluzione
            if (containerId) {
                setContainerResolved(containerId, true);
            }
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(`/shipper/containers/${containerId}?tab=documents`)} className="text-tertiary hover:text-primary">
                        <ArrowLeft className="size-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('triage.title')}</h1>
                        <p className="text-tertiary mt-1">{t('triage.subtitle')} {containerNumber}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Link to={`/shipper/containers/${containerId}/triage`} className="text-sm text-brand-secondary hover:text-brand-secondary_hover">
                        {t('triage.goToContainerTriage')}
                    </Link>
                    <Button
                        size="md"
                        color="primary"
                        iconLeading={MagicWand01}
                        onClick={startAIRun}
                    >
                        {t('triage.resolveWithAI')}
                    </Button>
                </div>
            </div>

            <Tabs defaultSelectedKey={tabItems[0].id}>
                <Tabs.List items={tabItems}>
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                {/* Tab: Visual */}
                <Tabs.Panel id="visual" className="space-y-4">
                    <div className="grid grid-cols-12 gap-4">
                        {/* Col 1 - 25% */}
                        <div className="col-span-12 md:col-span-3 rounded-xl bg-primary p-3 ring-1 ring-secondary">
                            <div className="space-y-2">
                                {documents.map((d) => {
                                    const hasWarn = docAlerts[d.id];
                                    return (
                                        <button
                                            key={d.id}
                                            onClick={() => setSelectedId(d.id)}
                                            className={`w-full text-left px-3 py-2 rounded-lg ring-1 ${selectedId === d.id ? 'bg-secondary ring-secondary' : 'ring-secondary hover:bg-secondary/60'}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-primary">{d.name}</span>
                                                {hasWarn ? (
                                                    <Badge color="warning" size="sm" className="inline-flex items-center gap-1">
                                                        <AlertCircle className="size-3" />
                                                        {t('triage.visual.check', { ns: 'pages:containers' })}
                                                    </Badge>
                                                ) : (
                                                    <Badge color="success" size="sm" className="inline-flex items-center gap-1">
                                                        <CheckCircle className="size-3" />
                                                        {t('triage.visual.ok', { ns: 'pages:containers' })}
                                                    </Badge>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Col 2 - 25% */}
                        <div className="col-span-12 md:col-span-3 rounded-xl bg-primary p-4 ring-1 ring-secondary">
                            <div className="space-y-2">
                                <div className="space-y-2">
                                    {COMMON_ATTRIBUTES.map((k) => (
                                        <div key={k} className="flex flex-col gap-1">
                                            <label className="text-xs text-tertiary">{t(`triage.attributes.${k}`, { ns: 'pages:documents' })}</label>
                                            <input
                                                className="rounded-md bg-primary ring-1 ring-secondary px-2 py-1 text-sm dark:text-white"
                                                value={String(formValues[k] ?? '')}
                                                onChange={(e) => setFormValues((prev) => ({ ...prev, [k]: e.target.value }))}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Col 3 - 50% */}
                        <div className="col-span-12 md:col-span-6 rounded-xl bg-primary p-2 ring-1 ring-secondary h-[70vh]">
                            {docFileUrl && <PdfViewer fileUrl={docFileUrl} polygons={[]} />}
                        </div>
                    </div>
                </Tabs.Panel>

                {/* Tab: Raw Data */}
                <Tabs.Panel id="raw" className="space-y-4">
                    <div className="rounded-xl bg-primary ring-1 ring-secondary p-4">
                        <h3 className="text-md font-semibold text-primary mb-3">{selectedDoc?.name}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs text-tertiary">{t('triage.raw.json')}</label>
                                <textarea
                                    className="w-full h-64 rounded-lg bg-white ring-1 ring-secondary p-2 font-mono text-xs"
                                    value={JSON.stringify(formValues, null, 2)}
                                    onChange={(e) => {
                                        try {
                                            const parsed = JSON.parse(e.target.value);
                                            setFormValues(parsed);
                                        } catch {
                                            // noop
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-tertiary">{t('triage.raw.highlightDiscrepancies')}</label>
                                <div className="mt-2 rounded-lg bg-secondary p-3 overflow-auto h-64">
                                    {COMMON_ATTRIBUTES.map((k) => {
                                        const warn = discrepancies.has(k);
                                        return (
                                            <div key={k} className={`text-xs py-1 px-2 rounded ${warn ? 'bg-warning-50 text-warning-900' : 'text-tertiary'}`}>
                                                <span className="font-mono mr-2">{t(`triage.attributes.${k}`, { ns: 'pages:documents' })}:</span>
                                                <span className="text-primary">{String(formValues[k] ?? '')}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </Tabs.Panel>
            </Tabs>

            {/* Modal Risoluzione AI */}
            <ModalOverlay isOpen={openAI} onOpenChange={onCloseModal}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary w-full max-w-xl">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-primary">{t('triage.modal.title')}</h3>
                            </div>
                            {progress < 100 && (
                                <div className="space-y-4">
                                    <p className="text-sm text-tertiary">{t('triage.modal.analyzing')}</p>
                                    <ProgressBar value={progress} min={0} max={100} />
                                    <div className="text-right text-xs text-tertiary">{progress}%</div>
                                </div>
                            )}
                            {progress === 100 && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-success-700">
                                        <CheckCircle className="size-5" />
                                        <span className="text-md font-semibold">{t('triage.modal.completed')}</span>
                                    </div>
                                    <p className="text-sm text-tertiary">{t('triage.modal.allResolved')}</p>
                                </div>
                            )}
                            <div className="mt-6 flex justify-end gap-2">
                                <Button color="secondary" onClick={() => onCloseModal(false)}>{t('triage.modal.close')}</Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </div>
    );
};

export default ShipperSingleDocumentTriagePage;


